/**
 * Typed service functions over the EdgeFinances backend.
 * Grouped by domain: auth, billing, admin, workspace.
 */
import { apiRequest } from './client';

export type ApiSubscriptionStatus = 'pending_payment' | 'active' | 'past_due';

export type PlanFeatures = Record<string, boolean>;

export type BillingTerm = 'monthly' | 'halfyearly' | 'yearly';

export type PlanPricingTier = { priceInr: number; enabled: boolean };

export type PlanPricingOption = {
  term: BillingTerm;
  label: string;
  months: number;
  priceInr: number;
  pricePaise?: number;
};

export type ApiPlan = {
  planId: string;
  name: string;
  description: string;
  priceInr: number;
  billingPeriod: 'monthly' | 'yearly';
  pricing?: Record<BillingTerm, PlanPricingTier>;
  pricingOptions?: PlanPricingOption[];
  defaultBillingTerm?: BillingTerm;
  features: PlanFeatures;
  featureItems?: PlanFeatures;
  razorpayPlanId?: string | null;
  mediaStorageLimitMb?: number;
  databaseStorageLimitMb?: number;
  /** Max branch ledger books (0 = unlimited) */
  maxLedgers?: number;
  /** @deprecated use mediaStorageLimitMb */
  storageLimitMb?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
};

export type ApiTrialOffer = {
  id: string;
  label: string;
  secretKey?: string;
  secretKeyHint?: string;
  planId: string;
  validFrom: string;
  validUntil: string;
  maxRedemptions: number | null;
  redemptionCount: number;
  isActive: boolean;
  cancelledAt: string | null;
  createdAt?: string;
};

export type ApiSubscriptionView = {
  subscriptionStatus: ApiSubscriptionStatus;
  subscriptionSource: 'trial' | 'paid' | 'demo' | null;
  subscriptionExpiresAt: string | null;
  daysRemaining: number | null;
  expiresLabel: string | null;
  planId: string | null;
  billingTerm?: BillingTerm | null;
  billingTermLabel?: string | null;
  termPriceInr?: number | null;
  plan: ApiPlan | null;
  isTrial: boolean;
  isExpired: boolean;
  mediaStorageLimitMb?: number;
  mediaStorageUsedMb?: number;
  mediaStorageRemainingMb?: number;
  mediaStoragePercentUsed?: number;
  mediaStorageFull?: boolean;
  databaseStorageLimitMb?: number;
  databaseStorageUsedMb?: number;
  databaseStorageRemainingMb?: number;
  databaseStoragePercentUsed?: number;
  databaseStorageFull?: boolean;
  ledgerCount?: number;
  maxLedgers?: number;
  ledgerLimitReached?: boolean;
  /** @deprecated use mediaStorageLimitMb */
  storageLimitMb?: number;
  storageUsedMb?: number;
  storageRemainingMb?: number;
  storagePercentUsed?: number;
  storageFull?: boolean;
};

export type ApiCompanyPartner = {
  id?: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  share: string;
};

export type ApiCompanyProfile = {
  legalName: string;
  displayName: string;
  tagline: string;
  email: string;
  phone: string;
  altPhone: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstin: string;
  registrationNo: string;
  logoUrl: string;
  partners: ApiCompanyPartner[];
};

export type ApiOrganization = {
  id: string;
  name: string;
  slug: string;
  adminEmail: string;
  subscriptionStatus: ApiSubscriptionStatus;
  planId: string | null;
  billingTerm?: BillingTerm | null;
  subscriptionExpiresAt?: string | null;
  subscriptionSource?: 'trial' | 'paid' | 'demo' | null;
  trialOfferId?: string | null;
  trialRedeemedAt?: string | null;
  accountHold?: boolean;
  accountHoldReason?: string;
  accountHeldAt?: string;
  lastWorkspaceAt?: string;
  companyProfile?: ApiCompanyProfile;
  createdAt: string;
  dataKeys?: number;
  whatsappMessagesInPeriod?: number;
};

export type AccessLevel = 'none' | 'read' | 'write';
export type DeskRole = 'super_admin' | 'admin' | 'ops' | 'auditor' | 'ledger_desk';
export type Permissions = Record<string, AccessLevel>;

export type ApiUser = {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'LEDGER';
  deskRole?: DeskRole;
  isOrgOwner?: boolean;
  isLedgerLogin?: boolean;
  permissions?: Permissions;
  uiPreferences?: { colorTheme?: string; darkMode?: boolean };
  createdAt?: string;
};

export type WorkspaceAdminRecord = {
  id: string;
  name: string;
  email: string;
  phone: string;
  deskRole: DeskRole;
  isOrgOwner: boolean;
  permissions: Permissions;
  createdAt?: string;
};

export type AuthResponse = {
  token: string;
  user: ApiUser;
  organization: ApiOrganization | null;
  ledgers?: LedgerPublic[];
  defaultLedgerId?: string | null;
  activeLedgerId?: string | null;
  ledger?: LedgerPublic;
};

export type LedgerReportConsolidation = {
  /** Include the Main Account (default ledger) in this ledger's Reports page. */
  includeMain: boolean;
  /** Other branch ledgers (clientIds) explicitly added to this ledger's Reports page. */
  ledgerIds: string[];
};

export type LedgerPublic = {
  id: string;
  name: string;
  isDefault: boolean;
  isDisabled: boolean;
  hasLogin?: boolean;
  loginEmail?: string | null;
  reportConsolidation?: LedgerReportConsolidation;
};

export const ledgerApi = {
  list() {
    return apiRequest<{ ledgers: LedgerPublic[] }>('/ledgers');
  },
  create(input: {
    id?: string;
    name: string;
    loginEmail?: string;
    loginPassword?: string;
  }) {
    return apiRequest<{ ledger: LedgerPublic }>('/ledgers', { method: 'POST', body: input });
  },
  update(
    id: string,
    input: {
      name?: string;
      isDisabled?: boolean;
      loginEmail?: string | null;
      loginPassword?: string;
      reportConsolidation?: Partial<LedgerReportConsolidation>;
    }
  ) {
    return apiRequest<{ ledger: LedgerPublic }>(`/ledgers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  },
  setDefault(id: string) {
    return apiRequest<{ ledger: LedgerPublic }>(`/ledgers/${encodeURIComponent(id)}/default`, {
      method: 'POST',
    });
  },
  remove(id: string) {
    return apiRequest<{ ok: boolean }>(`/ledgers/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  ensureDefault() {
    return apiRequest<{ ledger: LedgerPublic }>('/ledgers/ensure-default', { method: 'POST' });
  },
};

export const authApi = {
  register(input: { name: string; adminEmail: string; password: string; planId?: string | null; trialSecretKey?: string }) {
    return apiRequest<AuthResponse>('/auth/register', { method: 'POST', body: input, auth: false });
  },
  login(input: { email: string; password: string }) {
    return apiRequest<AuthResponse>('/auth/login', { method: 'POST', body: input, auth: false });
  },
  /**
   * Google Sign-In. `credential` is the Google ID token from the client.
   * Supplying `name` registers a new organization when no account exists yet;
   * omitting it is a pure login (404 if the Google email has no account).
   */
  google(input: { credential: string; name?: string; planId?: string | null; trialSecretKey?: string }) {
    return apiRequest<AuthResponse>('/auth/google', { method: 'POST', body: input, auth: false });
  },
  me() {
    return apiRequest<{
      user: ApiUser;
      organization: ApiOrganization | null;
      ledgers?: LedgerPublic[];
      defaultLedgerId?: string | null;
      activeLedgerId?: string | null;
    }>('/auth/me');
  },
  updateUiPreferences(input: { colorTheme?: string; darkMode?: boolean }) {
    return apiRequest<{ user: ApiUser }>('/auth/ui-preferences', { method: 'PATCH', body: input });
  },
  changePassword(input: { currentPassword: string; newPassword: string }) {
    return apiRequest<{ ok: boolean }>('/auth/change-password', { method: 'POST', body: input });
  },
  logout() {
    return apiRequest<{ ok: boolean }>('/auth/logout', { method: 'POST' });
  },
};

export type PaymentOrderResponse = {
  id: string;
  amount: number;
  currency: string;
  demo?: boolean;
};

export const billingApi = {
  listPlans() {
    return apiRequest<{ plans: ApiPlan[] }>('/billing/plans', { auth: false });
  },
  entitlementCatalog() {
    return apiRequest<{ catalog: { module: string; label: string; items: { id: string; label: string }[] }[] }>(
      '/billing/entitlements/catalog',
      { auth: false }
    );
  },
  startRegistration(input: {
    name: string;
    adminEmail: string;
    adminPhone?: string;
    password: string;
    planId: string;
    billingTerm?: BillingTerm;
    trialSecretKey?: string;
    autoPay?: boolean;
  }) {
    return apiRequest<
      | { skipPayment: true; token: string; user: ApiUser; organization: ApiOrganization | null }
      | {
          skipPayment: false;
          intentId: string;
          order: PaymentOrderResponse;
          razorpayKeyId: string | null;
          paymentMode?: 'live' | 'test' | 'demo';
          plan: ApiPlan;
          paymentConfigured: boolean;
        }
    >('/billing/start-registration', { method: 'POST', body: input, auth: false });
  },
  completeRegistration(input: {
    intentId: string;
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) {
    return apiRequest<{ token: string; user: ApiUser; organization: ApiOrganization | null }>(
      '/billing/complete-registration',
      { method: 'POST', body: input, auth: false }
    );
  },
  subscriptionOrder(input: {
    planId: string;
    billingTerm?: BillingTerm;
    action?: 'renew' | 'upgrade' | 'downgrade';
    autoPay?: boolean;
  }) {
    return apiRequest<
      | { skipPayment: true; organization: ApiOrganization; subscription: ApiSubscriptionView }
      | {
          skipPayment: false;
          paymentId: string;
          order: PaymentOrderResponse;
          action: string;
          razorpayKeyId: string | null;
          paymentMode?: 'live' | 'test' | 'demo';
          plan: ApiPlan;
          paymentConfigured: boolean;
        }
    >('/billing/subscription-order', { method: 'POST', body: input });
  },
  verifySubscriptionPayment(input: {
    paymentId: string;
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) {
    return apiRequest<{ organization: ApiOrganization; subscription: ApiSubscriptionView; action: string }>(
      '/billing/verify-subscription-payment',
      { method: 'POST', body: input }
    );
  },
  checkout(planId?: string) {
    return apiRequest<{ organization: ApiOrganization; subscription: ApiSubscriptionView }>('/billing/checkout', {
      method: 'POST',
      body: { planId },
    });
  },
  status() {
    return apiRequest<{
      subscriptionStatus: ApiSubscriptionStatus;
      planId: string | null;
      accountHold: boolean;
      organization: ApiOrganization;
      subscription: ApiSubscriptionView;
    }>('/billing/status');
  },
};

export type PlatformAnalytics = {
  total: number;
  activeBilling: number;
  pending: number;
  pastDue: number;
  onHold: number;
  signups7d: number;
  signups30d: number;
  activeLast14d: number;
  signupsInPeriod?: number;
  activeInPeriod?: number;
  totalDataKeys: number;
  planMix: Record<string, number>;
  smsMessagesTotal?: number;
  smsMessages30d?: number;
  smsMessages7d?: number;
  smsApiCostInr?: number;
  smsUnitCostInr?: number;
  /** @deprecated use sms* fields */
  whatsappMessagesTotal: number;
  whatsappMessages30d: number;
  whatsappMessages7d: number;
  whatsappApiCostInr: number;
  period?: { from: string | null; to: string | null };
};

export type AdminStorageSnapshot = {
  mediaStorageLimitMb: number;
  mediaStorageUsedMb: number;
  mediaStoragePercentUsed: number;
  mediaStorageFull: boolean;
  databaseStorageLimitMb: number;
  databaseStorageUsedMb: number;
  databaseStoragePercentUsed: number;
  databaseStorageFull: boolean;
};

export type AdminTenantUsageSummary = {
  organizationId: string;
  name: string;
  adminEmail: string;
  slug: string;
  planId: string | null;
  planName: string | null;
  billingTerm: string | null;
  subscriptionStatus: string;
  subscriptionSource: string | null;
  subscriptionExpiresAt: string | null;
  accountHold: boolean;
  lastWorkspaceAt: string | null;
  createdAt: string;
  storage: AdminStorageSnapshot;
  records: {
    workspaceData: number;
    finance: number;
    handloans: number;
    emiPayments: number;
    total: number;
  };
  smsMessagesInPeriod?: number;
  smsApiCostInr?: number;
  smsUnitCostInr?: number;
  /** @deprecated use smsMessagesInPeriod */
  whatsappMessagesInPeriod: number;
  whatsappApiCostInr: number;
};

export type AdminOrgUsage = {
  organization: ApiOrganization;
  plan: ApiPlan | null;
  storage: AdminStorageSnapshot;
  usage: {
    admins: number;
    records: {
      workspaceData: number;
      finance: number;
      handloans: number;
      emiPayments: number;
    };
    mongoStorage: {
      estimatedBytes: number;
      estimatedMb: number;
      byCollectionBytes: {
        workspaceData: number;
        finance: number;
        handloans: number;
        emiPayments: number;
      };
    };
    sms: {
      totalSent: number;
      periodSent?: number;
      last7d: number;
      last30d: number;
      byTrigger: {
        manual: number;
        schedule: number;
        cron: number;
      };
      apiCostInr: number;
      periodCostInr?: number;
      unitCostInr?: number;
      failed?: number;
      lastSentAt: string | null;
      sort?: SmsUsageSort;
      daily?: { date: string; count: number; costInr: number }[];
      recent?: {
        id: string;
        at: string | null;
        status: string;
        trigger: string;
        phone: string;
        customerName: string;
        refId: string;
        dueDate: string;
        amount: number;
        requestId: string;
        errorMessage: string;
      }[];
      period?: {
        from: string | null;
        to: string | null;
        fromYmd?: string | null;
        toYmd?: string | null;
        filtered?: boolean;
      };
    };
    /** @deprecated use usage.sms — same shape for older clients */
    whatsapp: AdminOrgUsage['usage']['sms'];
  };
};

export const adminApi = {
  listOrgs(params?: { from?: string; to?: string }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{ organizations: ApiOrganization[]; period?: { from: string | null; to: string | null } }>(
      `/admin/orgs${suffix}`
    );
  },
  analytics(params?: { from?: string; to?: string }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<PlatformAnalytics>(`/admin/analytics${suffix}`);
  },
  orgUsage(orgId: string, params?: { from?: string; to?: string; sort?: SmsUsageSort; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.sort) qs.set('sort', params.sort);
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<AdminOrgUsage>(`/admin/orgs/${encodeURIComponent(orgId)}/usage${suffix}`);
  },
  listTenantUsage(params?: { from?: string; to?: string }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{ tenants: AdminTenantUsageSummary[]; period?: { from: string | null; to: string | null } }>(
      `/admin/usage/tenants${suffix}`
    );
  },
  setHold(orgId: string, held: boolean, reason?: string) {
    return apiRequest<{ organization: ApiOrganization }>(`/admin/orgs/${encodeURIComponent(orgId)}/hold`, {
      method: 'POST',
      body: { held, reason },
    });
  },
  updateOrg(orgId: string, patch: Partial<Pick<ApiOrganization, 'name' | 'slug' | 'planId' | 'subscriptionStatus' | 'billingTerm' | 'subscriptionExpiresAt'>>) {
    return apiRequest<{ organization: ApiOrganization }>(`/admin/orgs/${encodeURIComponent(orgId)}`, {
      method: 'PATCH',
      body: patch,
    });
  },
  extendSubscription(
    orgId: string,
    body: { addMonths?: number; billingTerm?: BillingTerm; planId?: string }
  ) {
    return apiRequest<{
      organization: ApiOrganization;
      plan: ApiPlan | null;
      subscription: ApiSubscriptionView;
    }>(`/admin/orgs/${encodeURIComponent(orgId)}/extend-subscription`, {
      method: 'POST',
      body,
    });
  },
  deleteOrg(orgId: string) {
    return apiRequest<{ ok: boolean; orgName?: string }>(`/admin/orgs/${encodeURIComponent(orgId)}`, { method: 'DELETE' });
  },
  listPlans() {
    return apiRequest<{ plans: ApiPlan[] }>('/admin/plans');
  },
  createPlan(input: Omit<ApiPlan, 'createdAt' | 'updatedAt'>) {
    return apiRequest<{ plan: ApiPlan }>('/admin/plans', { method: 'POST', body: input });
  },
  updatePlan(planId: string, patch: Partial<ApiPlan>) {
    return apiRequest<{ plan: ApiPlan }>(`/admin/plans/${encodeURIComponent(planId)}`, { method: 'PATCH', body: patch });
  },
  deletePlan(planId: string) {
    return apiRequest<{ ok: boolean; planId: string }>(`/admin/plans/${encodeURIComponent(planId)}`, { method: 'DELETE' });
  },
  listTrials() {
    return apiRequest<{ trials: ApiTrialOffer[] }>('/admin/trials');
  },
  createTrial(input: {
    label: string;
    planId: string;
    validFrom: string;
    validUntil: string;
    maxRedemptions?: number | null;
  }) {
    return apiRequest<{ trial: ApiTrialOffer }>('/admin/trials', { method: 'POST', body: input });
  },
  cancelTrial(trialId: string) {
    return apiRequest<{ trial: ApiTrialOffer }>(`/admin/trials/${encodeURIComponent(trialId)}/cancel`, { method: 'POST' });
  },
  deleteTrial(trialId: string) {
    return apiRequest<{ ok: boolean; id: string }>(`/admin/trials/${encodeURIComponent(trialId)}`, { method: 'DELETE' });
  },
  cancelOrgTrial(orgId: string) {
    return apiRequest<{ organization: ApiOrganization }>(`/admin/orgs/${encodeURIComponent(orgId)}/cancel-trial`, {
      method: 'POST',
    });
  },
  listDeletionRequests(status: 'pending' | 'all' = 'pending') {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiRequest<{ requests: AccountDeletionRequestPublic[] }>(`/admin/deletion-requests${qs}`);
  },
  approveDeletionRequest(id: string, note?: string) {
    return apiRequest<{ request: AccountDeletionRequestPublic; deleted?: boolean; orgName?: string }>(
      `/admin/deletion-requests/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: { note } }
    );
  },
  rejectDeletionRequest(id: string, note: string) {
    return apiRequest<{ request: AccountDeletionRequestPublic }>(
      `/admin/deletion-requests/${encodeURIComponent(id)}/reject`,
      { method: 'POST', body: { note } }
    );
  },
  deleteDeletionRequest(id: string) {
    return apiRequest<{ ok: boolean; id: string }>(`/admin/deletion-requests/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};

export type AccountDeletionRequestPublic = {
  id: string;
  requestType: 'organization';
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  organizationId: string;
  orgName: string;
  orgAdminEmail: string;
  requesterEmail: string;
  requesterName: string;
  reason: string;
  reviewNote: string | null;
  reviewedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
};

export const accountApi = {
  getDeletionRequest() {
    return apiRequest<{ request: AccountDeletionRequestPublic | null }>('/account/deletion-request');
  },
  submitDeletionRequest(body: { reason: string; confirmName: string }) {
    return apiRequest<{ request: AccountDeletionRequestPublic }>('/account/deletion-request', {
      method: 'POST',
      body,
    });
  },
  cancelDeletionRequest() {
    return apiRequest<{ request: AccountDeletionRequestPublic }>('/account/deletion-request', {
      method: 'DELETE',
    });
  },
};

export type WorkspaceItem = { key: string; value: unknown; updatedAt?: string };

export const workspaceApi = {
  getState() {
    return apiRequest<{ items: WorkspaceItem[] }>('/workspace/state');
  },
  getKey(key: string) {
    return apiRequest<WorkspaceItem>(`/workspace/kv/${encodeURIComponent(key)}`);
  },
  putKey(key: string, value: unknown) {
    return apiRequest<WorkspaceItem>(`/workspace/kv/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: { value },
    });
  },
  deleteKey(key: string) {
    return apiRequest<{ ok: boolean }>(`/workspace/kv/${encodeURIComponent(key)}`, { method: 'DELETE' });
  },
  bulkPut(items: WorkspaceItem[]) {
    return apiRequest<{ updated: number }>('/workspace/bulk', { method: 'PUT', body: { items } });
  },
};

export const companyApi = {
  get() {
    return apiRequest<{ profile: ApiCompanyProfile }>('/workspace/company-profile');
  },
  update(profile: Partial<ApiCompanyProfile>) {
    return apiRequest<{ profile: ApiCompanyProfile }>('/workspace/company-profile', {
      method: 'PUT',
      body: { profile },
    });
  },
};

export const workspaceAdminsApi = {
  list() {
    return apiRequest<{ admins: WorkspaceAdminRecord[] }>('/workspace/admins');
  },
  create(input: {
    name: string;
    email: string;
    phone?: string;
    deskRole?: DeskRole;
    password?: string;
    permissions?: Permissions;
  }) {
    return apiRequest<{ admin: WorkspaceAdminRecord; temporaryPassword?: string }>('/workspace/admins', {
      method: 'POST',
      body: input,
    });
  },
  update(
    id: string,
    patch: {
      name?: string;
      phone?: string;
      deskRole?: DeskRole;
      password?: string;
      permissions?: Permissions;
      resetPermissionsToRole?: boolean;
    }
  ) {
    return apiRequest<{ admin: WorkspaceAdminRecord }>(`/workspace/admins/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
    });
  },
  remove(id: string) {
    return apiRequest<{ ok: boolean }>(`/workspace/admins/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

/** Generic record shape shared by finance + hand-loan resources. */
export type ApiRecord = {
  id: string;
  createdAt: string;
  updatedAt: string;
  source: 'api' | 'local';
  data: Record<string, unknown>;
};

export const financeApi = {
  list() {
    return apiRequest<{ records: ApiRecord[] }>('/finance');
  },
  lookupMainByHa(haNumber: string) {
    const q = encodeURIComponent(haNumber.trim());
    return apiRequest<{ found: boolean; record?: ApiRecord }>(`/finance/lookup-main?haNumber=${q}`);
  },
  upsert(record: { id: string; source?: string; data: Record<string, unknown> }) {
    return apiRequest<{ record: ApiRecord }>('/finance', { method: 'POST', body: record });
  },
  remove(id: string) {
    return apiRequest<{ ok: boolean }>(`/finance/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

export const handLoanApi = {
  list() {
    return apiRequest<{ records: ApiRecord[] }>('/handloans');
  },
  upsert(record: { id: string; source?: string; data: Record<string, unknown> }) {
    return apiRequest<{ record: ApiRecord }>('/handloans', { method: 'POST', body: record });
  },
  remove(id: string) {
    return apiRequest<{ ok: boolean }>(`/handloans/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

export const emiApi = {
  list() {
    return apiRequest<{ payments: Record<string, unknown>[] }>('/emi-payments');
  },
  create(payment: Record<string, unknown>) {
    return apiRequest<{ payment: Record<string, unknown> }>('/emi-payments', {
      method: 'POST',
      body: payment,
    });
  },
  remove(id: string) {
    return apiRequest<{ ok: boolean }>(`/emi-payments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

export const consultancyApi = {
  get() {
    return apiRequest<{ bundle: Record<string, unknown> }>('/consultancy');
  },
  put(bundle: Record<string, unknown>) {
    return apiRequest<{ bundle: Record<string, unknown> }>('/consultancy', {
      method: 'PUT',
      body: { bundle },
    });
  },
};

export const accountingApi = {
  get() {
    return apiRequest<{ bundle: Record<string, unknown> }>('/accounting');
  },
  put(bundle: Record<string, unknown>) {
    return apiRequest<{ bundle: Record<string, unknown> }>('/accounting', {
      method: 'PUT',
      body: { bundle },
    });
  },
};

export type CustomerAccountPublic = {
  id: string;
  name: string;
  phone: string;
  email: string;
  createdAt?: string;
};

export type CustomerCompany = {
  name: string;
  profile: ApiCompanyProfile;
};

export type CustomerOverview = {
  account: CustomerAccountPublic;
  company: CustomerCompany | null;
  finance: ApiRecord[];
  handloans: ApiRecord[];
  payments: Record<string, unknown>[];
};

export const customerApi = {
  /** Admin: create/link a borrower login for a finance record. */
  provision(input: { name: string; phone: string; recordId?: string }) {
    return apiRequest<{ account: CustomerAccountPublic; email: string; password: string; created: boolean }>(
      '/customers/provision',
      { method: 'POST', body: input }
    );
  },
  /** Public: borrower sign-in (returns a customer-scoped token). */
  login(input: { email: string; password: string }) {
    return apiRequest<{ token: string; account: CustomerAccountPublic; company: CustomerCompany | null }>(
      '/customers/login',
      { method: 'POST', body: input, auth: false }
    );
  },
  /** Borrower: read-only EMI overview (uses the customer token). */
  overview(token: string) {
    return apiRequest<CustomerOverview>('/customers/me/overview', { token });
  },
};

export type UploadedImage = {
  /** Short asset filename for DB storage (shop_<id>.jpg / customer_<id>.jpg). */
  filename?: string;
  /** Resolved CDN URL for immediate preview (IMAGE_BASE_URL + filename). */
  url: string;
  publicId: string;
  width?: number;
  height?: number;
  bytes?: number;
  format?: string;
};

export type SmsUsageSort = 'date_asc' | 'date_desc' | 'count_asc' | 'count_desc';

export type SmsUsageReport = {
  unitCostInr: number;
  totalSent: number;
  periodSent?: number;
  failed: number;
  last7d: number;
  last30d: number;
  estimatedCostInr: number;
  periodCostInr?: number;
  estimatedCostLast30dInr: number;
  byTrigger: { manual: number; schedule: number; cron: number; other: number };
  daily: { date: string; count: number; costInr: number }[];
  sort?: SmsUsageSort;
  recent: {
    id: string;
    at: string | null;
    status: string;
    trigger: string;
    phone: string;
    customerId: string;
    customerName: string;
    refId: string;
    dueDate: string;
    amount: number;
    requestId: string;
    errorMessage: string;
  }[];
  lastSentAt: string | null;
  period: {
    from: string | null;
    to: string | null;
    fromYmd?: string | null;
    toYmd?: string | null;
    filtered?: boolean;
  };
};

export const smsApi = {
  status() {
    return apiRequest<{
      configured: boolean;
      provider?: string;
      templatePreview?: string;
      templateVarsPreview?: Record<string, string>;
    }>('/sms/status');
  },
  usage(params?: { from?: string; to?: string; limit?: number; sort?: SmsUsageSort }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.sort) qs.set('sort', params.sort);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<SmsUsageReport>(`/sms/usage${suffix}`);
  },
  sendPaymentReminder(input: {
    phone: string;
    customerName: string;
    amount: number;
    dueDate: string;
    /** HA number — used with finance name as Ref ID in the DLT SMS */
    haNumber?: string;
    financeName?: string;
    refId?: string;
    recordId?: string;
    kind?: 'finance' | 'handloan';
    facilityTitle?: string;
    daysBefore?: number;
  }) {
    return apiRequest<{ ok: boolean; messageId?: string }>('/sms/send-payment-reminder', {
      method: 'POST',
      body: input,
    });
  },
  runReminders() {
    return apiRequest<{ ok: boolean; sent: number; skipped: number; failed?: number; message?: string }>(
      '/sms/run-reminders',
      { method: 'POST' }
    );
  },
};

/** @deprecated Use smsApi */
export const whatsappApi = smsApi;

export type AuditLogChange = {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
};

export type AuditLogRecord = {
  id: string;
  organizationId: string | null;
  ledgerId: string | null;
  actorId: string;
  actorEmail: string;
  actorName: string;
  actorRole: string;
  actorDeskRole: string;
  actorType: string;
  action: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  summary: string;
  reason: string;
  changes: AuditLogChange[];
  metadata: Record<string, unknown>;
  ipAddress: string;
  userAgent: string;
  device: string;
  createdAt?: string;
  updatedAt?: string;
};

export type AuditLogStats = {
  total: number;
  today: number;
  logins: number;
  updates: number;
  security: number;
  byAction: Record<string, number>;
};

export const auditLogsApi = {
  list(params?: {
    page?: number;
    limit?: number;
    from?: string;
    to?: string;
    action?: string;
    entityType?: string;
    actorId?: string;
    search?: string;
  }) {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.action) qs.set('action', params.action);
    if (params?.entityType) qs.set('entityType', params.entityType);
    if (params?.actorId) qs.set('actorId', params.actorId);
    if (params?.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{
      logs: AuditLogRecord[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/audit-logs${suffix}`);
  },
  stats(params?: { from?: string; to?: string }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<AuditLogStats>(`/audit-logs/stats${suffix}`);
  },
  get(id: string) {
    return apiRequest<{ log: AuditLogRecord }>(`/audit-logs/${encodeURIComponent(id)}`);
  },
};

export type FinancialYearRecord = {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closing' | 'closed';
  openingBalances: Record<string, number>;
  closingBalances: Record<string, number>;
  lockedAt?: string | null;
  closedBy?: string;
  carriedForwardTo?: string | null;
};

export type ErpTrialBalanceRow = {
  code: string;
  name: string;
  type: string;
  debit: number;
  credit: number;
  balance: number;
  side: string;
  opening?: number;
  periodDebit?: number;
  periodCredit?: number;
};

export type ErpCheck = {
  code: string;
  status: 'ok' | 'warning' | 'error' | 'info';
  message: string;
  amount?: number;
};

export type ErpBalanceSheetRow = { code?: string; name: string; amount: number };

export type ErpSnapshot = {
  period: { from: string | null; to: string | null };
  financialYear: FinancialYearRecord | null;
  booksStart?: string | null;
  disclaimer: string;
  openingBalances?: {
    cash: number;
    bank: number;
    loansReceivable: number;
    inventory: number;
    employeeAdvances: number;
    loansTaken: number;
    creditors: number;
    capital: number;
    profitBroughtForward: number;
    openingDifference: number;
  };
  checks?: ErpCheck[];
  trialBalance: {
    rows: ErpTrialBalanceRow[];
    totalDebit: number;
    totalCredit: number;
    closingDebit?: number;
    closingCredit?: number;
    difference: number;
    balanced: boolean;
  };
  profitAndLoss: {
    income: { code: string; name: string; amount: number }[];
    expenses: { code: string; name: string; amount: number }[];
    totalIncome: number;
    totalExpenses: number;
    netProfit: number;
    basis?: string;
  };
  balanceSheet: {
    assets: ErpBalanceSheetRow[];
    liabilities: ErpBalanceSheetRow[];
    equity: ErpBalanceSheetRow[];
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
    totalLiabilitiesAndEquity?: number;
    netWorth: number;
    difference?: number;
    balanced?: boolean;
  };
  cashFlow: {
    openingCash: number;
    openingBank?: number;
    collections: number;
    cashCollections?: number;
    bankCollections?: number;
    cashSales?: number;
    bankSales?: number;
    cashDisbursements?: number;
    bankDisbursements?: number;
    cashPurchases?: number;
    bankPurchases?: number;
    cashOther?: number;
    bankOther?: number;
    otherIncome: number;
    expenses: number;
    cashExpenses?: number;
    bankExpenses?: number;
    salary: number;
    closingCash: number;
    closingBank?: number;
    closingTotal?: number;
  };
  dayBook: {
    date: string;
    label: string;
    items: { time: string; description: string; type: string; amount: number; entryId: string; direction?: 'in' | 'out' | 'transfer' }[];
    dayTotal: number;
    moneyIn?: number;
    moneyOut?: number;
  }[];
  ledgerBooks: {
    code: string;
    name: string;
    entries: { date: string | null; description: string; debit: number; credit: number; balance: number; entryId: string }[];
    balance: number;
  }[];
  journalEntries: {
    clientId: string;
    entryDate: string;
    description: string;
    lines: { account: string; accountName: string; debit: number; credit: number }[];
    sourceType?: string;
    sourceId?: string;
    isReversal?: boolean;
    reversalEntryId?: string;
  }[];
  reconciliation: {
    cashBook: number;
    bankBook: number;
    booksTotal?: number;
    flowCash?: number;
    flowBank?: number;
    flowTotal?: number;
    cashBankGap?: number;
    difference: number;
    needsVerification: boolean;
    status: string;
  };
  /** EMIs due by the period end but not received. Memo only — income is booked when cash is received. */
  receivablesMemo?: {
    asOf: string;
    overdueLoans: number;
    overdueAmount: number;
    overdueInterest: number;
  } | null;
  financialHealth: {
    profitMargin: number;
    collectionPct: number;
    outstandingPct: number;
    expensePct: number;
    overduePct?: number;
    dueSlots?: number;
    overdueSlots?: number;
    badLoansPct: number;
    cashFlowPositive: boolean;
    businessScore: number;
  };
  aiAuditor: {
    insights: { type: string; message: string }[];
    recommendations: string[];
    generatedAt: string;
  };
  portfolio: {
    outstanding: number;
    employeeAdvances: number;
    inventoryValue: number;
    activeFinanceLoans: number;
    activeHandLoans: number;
  };
};

export type ConsolidatedMonth = {
  month: string;
  receipts: number;
  installments: number;
  investments: number;
  expenses: number;
  moneyIn: number;
  moneyOut: number;
};

/** Aggregate-only figures for one ledger (or the combined total). */
export type ConsolidatedSummary = {
  receipts: { count: number; total: number; cash: number; bank: number; outPayments?: number };
  installments: { count: number; emiCollected: number; lateFees: number; interest: number; principal: number };
  investments: { buyCount: number; invested: number; saleCount: number; returns: number };
  expenses: { count: number; total: number; byCategory: { category: string; amount: number }[] };
  payments: {
    loanDisbursed: number;
    loanCount: number;
    cashIn: number;
    bankIn: number;
    cashOut: number;
    bankOut: number;
    moneyIn: number;
    moneyOut: number;
    net: number;
  };
  monthly: ConsolidatedMonth[];
};

export type ConsolidatedAccount = {
  id: string;
  name: string;
  /** main = Main Account, self = the ledger being viewed, extra = explicitly configured branch ledger */
  role: 'main' | 'self' | 'extra';
  summary: ConsolidatedSummary;
};

export type ConsolidatedReport =
  | {
      enabled: false;
      reason: 'main_account' | 'not_configured';
      ledger: { id: string; name: string; isDefault: boolean };
    }
  | {
      enabled: true;
      ledger: { id: string; name: string; isDefault: boolean };
      range: { from: string | null; to: string | null };
      accounts: ConsolidatedAccount[];
      combined: ConsolidatedSummary;
      generatedAt: string;
    };

export const reportsApi = {
  /** `from` / `to` are absolute instants (start / end of the selected local days). */
  consolidated(params: { from?: string | null; to?: string | null; tz?: number }) {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.tz != null) qs.set('tz', String(params.tz));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<ConsolidatedReport>(`/reports/consolidated${suffix}`);
  },
};

export type FinancialYearWindow = { startYear: number; label: string; startYmd: string; endYmd: string };

export type TenantFinancialYear = {
  startMonth: number;
  selectedStartYear: number | null;
  current: FinancialYearWindow;
  selected: FinancialYearWindow;
  followsCurrent: boolean;
  updatedAt: string | null;
  updatedBy: string;
};

/** Tenant-wide financial-year configuration (Settings → Financial year). */
export const financialYearApi = {
  get() {
    return apiRequest<{ financialYear: TenantFinancialYear; canManage: boolean }>('/financial-year');
  },
  update(input: { startMonth?: number; selectedStartYear?: number | null }) {
    return apiRequest<{ financialYear: TenantFinancialYear; canManage: boolean }>('/financial-year', {
      method: 'PUT',
      body: input,
    });
  },
};

export const erpApi = {
  listFinancialYears() {
    return apiRequest<{ years: FinancialYearRecord[] }>('/erp/financial-years');
  },
  getCurrentFinancialYear() {
    return apiRequest<{ year: FinancialYearRecord }>('/erp/financial-years/current');
  },
  createFinancialYear(input: {
    label?: string;
    startDate?: string;
    endDate?: string;
    openingBalances?: Record<string, number>;
  }) {
    return apiRequest<{ year: FinancialYearRecord }>('/erp/financial-years', { method: 'POST', body: input });
  },
  updateFinancialYear(id: string, patch: { openingBalances?: Record<string, number>; label?: string }) {
    return apiRequest<{ year: FinancialYearRecord }>(`/erp/financial-years/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
    });
  },
  closeFinancialYear(id: string) {
    return apiRequest<{ closed: FinancialYearRecord; next: FinancialYearRecord; snapshot: ErpSnapshot }>(
      `/erp/financial-years/${encodeURIComponent(id)}/close`,
      { method: 'POST' }
    );
  },
  /** Owner only; the latest closed year can be reopened, with a reason for the audit log. */
  reopenFinancialYear(id: string, reason: string) {
    return apiRequest<{ year: FinancialYearRecord }>(
      `/erp/financial-years/${encodeURIComponent(id)}/reopen`,
      { method: 'POST', body: { reason } }
    );
  },
  snapshot(params?: { from?: string; to?: string; fyId?: string; useCurrentFy?: boolean }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.fyId) qs.set('fyId', params.fyId);
    if (params?.useCurrentFy) qs.set('useCurrentFy', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<ErpSnapshot>(`/erp/snapshot${suffix}`);
  },
  reverseJournal(clientId: string, reason?: string) {
    return apiRequest<{ original: unknown; reversal: unknown }>(
      `/erp/journal/${encodeURIComponent(clientId)}/reverse`,
      { method: 'POST', body: { reason } }
    );
  },
};

export const uploadsApi = {
  /**
   * Upload a data URL / base64 image.
   * kind company/shop → shop_<orgId>; customer → customer_<customerId> (overwrite).
   */
  image(
    image: string,
    opts?: string | { kind?: 'company' | 'shop' | 'customer' | 'consultancy'; customerId?: string; folder?: string }
  ) {
    const body =
      typeof opts === 'string'
        ? { image, folder: opts, kind: opts }
        : {
            image,
            kind: opts?.kind || opts?.folder || 'company',
            folder: opts?.folder || opts?.kind,
            customerId: opts?.customerId,
          };
    return apiRequest<UploadedImage>('/uploads/image', {
      method: 'POST',
      body,
    });
  },
};

export {
  ApiError,
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  hasAuthToken,
  getCustomerToken,
  setCustomerToken,
  clearCustomerToken,
  hasCustomerToken,
  API_BASE_URL,
} from './client';
